import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import InputLabel from "@/atoms-react/input/Input.react.tsx";
import { PasswordInput } from "@/atoms-react/input/password-input.react";
import { PasswordRequirements } from "@/atoms-react/input/password-requirements.react.tsx";
import { jwtDecode } from "@/features/auth/invalid-token-error";
import { jwtPayload } from "@/features/auth/types";
import { setJwt } from "@/features/auth/utils";
import useChangePasswordMutation from "@/features/security/hooks/use-change-password";
import { useAppDispatch } from "@/redux/hooks";
import { setExpirationToken, setLinkedCompanies, setRoles } from "@/redux/slices/userSlice";

enum ChangePasswordStep {
  Email,
  PasswordAndOTP,
  Success
}

type ChangePasswordProps = {
  onValidated?: () => void;
};

export const ChangePassword = ({ onValidated = () => {} }: ChangePasswordProps) => {
  const { t } = useTranslation(["pass"]);
  const [password, setPassword] = useState<string>();
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
  const [step] = useState<ChangePasswordStep>(ChangePasswordStep.PasswordAndOTP);
  const { mutateAsync: changePassword, isPending: isChangingPassword } =
    useChangePasswordMutation();
  const [oldPassword, setOldPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const dispatch = useAppDispatch();

  const onSubmitChangePassword = async () => {
    try {
      if (!password || !oldPassword) {
        throw new Error(t("olkypass.forgetPasswordPage.notifications.error.reset"));
      }
      const ret = await changePassword({ password, old_password: oldPassword });
      if (ret.success && ret.jwt) {
        const decoded: jwtPayload = jwtDecode(ret.jwt);
        const { exp, roles, linked_companies } = decoded;
        if (roles) dispatch(setRoles({ roles: roles }));
        if (linked_companies) dispatch(setLinkedCompanies({ linkedCompanies: linked_companies }));
        dispatch(setExpirationToken(Number(exp)));
        setJwt({ token: ret.jwt });
      }
      onValidated();
    } catch (e) {
      if (e instanceof Error) toast.error(e.message);
    }
  };

  return (
    <div>
      {step === ChangePasswordStep.PasswordAndOTP && (
        <Column>
          <div className="mb-12 flex grow flex-col">
            <InputLabel
              autoFocus
              type="password"
              placeholder={t("flow.id_control.input.placeholder_password")}
              value={oldPassword}
              onChange={(value: string) => setOldPassword(value)}
              label={t("olkypass.securityPage.passwordSection.input.current_pwd")}
              className={"mb-1"}
            />

            <PasswordInput
              onChange={({ password }) => {
                setPassword(password);
              }}
              label={t("olkypass.securityPage.passwordSection.input.new_pwd")}
            />

            <InputLabel
              type="password"
              placeholder={t("flow.id_control.input.placeholder_password")}
              value={confirmPassword}
              onChange={(value: string) => setConfirmPassword(value)}
              label={t("olkypass.securityPage.passwordSection.input.confirm_new_pwd")}
              className={"mb-1"}
            />
            <PasswordRequirements
              password={password ?? ""}
              confirmPassword={confirmPassword}
              setValidity={({ valid }) => setIsPasswordValid(valid)}
            />
          </div>

          <div className="shrink-0">
            <Button
              shortcut="enter"
              onClick={onSubmitChangePassword}
              disabled={
                !isPasswordValid ||
                !oldPassword ||
                !confirmPassword ||
                confirmPassword !== password ||
                oldPassword?.length < 8
              }
              loading={isChangingPassword}
            >
              {t("flow.id_control.input.save")}
            </Button>
          </div>
        </Column>
      )}
    </div>
  );
};
